import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';
import {
  AtokuAccessCredentials,
  AtokuConsolePricingProfileTypeResponse,
  AtokuService
} from '../../services/atoku.service';
import {FlexLayoutModule} from "@angular/flex-layout";

type PricingProfileTypeItem = AtokuConsolePricingProfileTypeResponse;

@Component({
  selector: 'app-atoku-company-type-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    FlexLayoutModule
  ],
  templateUrl: './atoku-company-type-select.component.html',
  styleUrl: './atoku-company-type-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AtokuCompanyTypeSelectComponent),
      multi: true
    }
  ]
})
export class AtokuCompanyTypeSelectComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  /** Required so the component can call getPricingProfileTypes(...) */
  @Input() credentials: AtokuAccessCredentials | null = null;

  /** Optional UI bits */
  @Input() label = 'Company Type';
  @Input() placeholder = 'Select company type';

  items: PricingProfileTypeItem[] = [];

  readonly control = new FormControl<string | null>(null);

  private readonly destroy$ = new Subject<void>();

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly atokuService: AtokuService) {
    this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.onChange(value);
      this.onTouched();
    });
  }

  ngOnChanges(): void {
    this.loadItems();
  }

  private loadItems(): void {
    if (!this.credentials) {
      this.items = [];
      return;
    }

    this.atokuService.getPricingProfileTypes(this.credentials).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.items = res ?? [];

        // If current value is no longer valid, clear it.
        const current = this.control.value;
        if (current && !this.items.some(x => x.code === current)) {
          this.control.setValue(null, { emitEvent: true });
        }
      },
      error: () => {
        this.items = [];
      }
    });
  }

  writeValue(value: string | null): void {
    this.control.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.control.disable({ emitEvent: false });
    else this.control.enable({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
